import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Eye, Smartphone, Monitor, Clock, TrendingUp, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function StatCard({ label, value, sublabel, icon: Icon, color = "#1E3A8A" }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white p-5" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#475569] font-bold">{label}</div>
          <div className="mt-2 text-3xl font-black text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{value}</div>
          {sublabel && <div className="mt-1 text-xs text-[#475569]">{sublabel}</div>}
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/analytics/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Supprimer définitivement toutes les statistiques enregistrées ? Cette action est irréversible.")) return;
    try {
      await api.delete("/analytics/reset");
      toast.success("Statistiques réinitialisées");
      load();
    } catch { toast.error("Erreur réinitialisation"); }
  };

  if (loading) return <div className="text-sm text-[#475569]">Chargement des statistiques…</div>;
  if (!stats) return <div className="text-sm text-[#DC2626]">Impossible de charger les statistiques.</div>;

  const maxDaily = Math.max(...stats.daily.map((d) => d.views), 1);

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Top row */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-widest">Aujourd'hui</h3>
          <Button variant="ghost" size="sm" onClick={load} data-testid="analytics-refresh">
            <RotateCcw size={14} className="mr-1" /> Actualiser
          </Button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <StatCard label="Visiteurs uniques" value={stats.today.unique_visitors} sublabel={`Hier : ${stats.yesterday.unique_visitors}`} icon={Users} color="#D97706" />
          <StatCard label="Pages vues" value={stats.today.pageviews} sublabel={`Hier : ${stats.yesterday.pageviews}`} icon={Eye} color="#1E3A8A" />
          <StatCard label="Sur mobile" value={`${stats.today.mobile_ratio}%`} sublabel={`${100 - stats.today.mobile_ratio}% sur ordinateur`} icon={Smartphone} color="#059669" />
        </div>
      </div>

      {/* 7 days */}
      <div>
        <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-widest mb-3">7 derniers jours</h3>
        <div className="grid sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Visiteurs" value={stats.week.unique_visitors} icon={Users} color="#D97706" />
          <StatCard label="Pages vues" value={stats.week.pageviews} icon={Eye} color="#1E3A8A" />
          <StatCard label="Mobile" value={`${stats.week.mobile_ratio}%`} icon={Smartphone} color="#059669" />
          <StatCard label="Pic de visite" value={stats.peak_hour != null ? `${stats.peak_hour}h` : "—"} sublabel="Heure la plus active" icon={Clock} color="#7C3AED" />
        </div>

        {/* Daily chart */}
        <div className="rounded-lg border border-[#E2E8F0] bg-white p-5" data-testid="analytics-daily-chart">
          <div className="text-[11px] uppercase tracking-widest text-[#475569] font-bold mb-4">Évolution quotidienne</div>
          <div className="flex items-end gap-2 h-40">
            {stats.daily.map((d, i) => {
              const dt = new Date(d.date + "T00:00:00");
              const dayLabel = dt.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });
              const height = (d.views / maxDaily) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="w-full flex-1 flex items-end" style={{ minHeight: "4px" }}>
                    <div
                      className="w-full bg-gradient-to-t from-[#1E3A8A] to-[#3B82F6] rounded-t transition-all hover:from-[#D97706] hover:to-[#F59E0B]"
                      style={{ height: `${height}%`, minHeight: d.views > 0 ? "4px" : "0" }}
                      title={`${d.views} vues · ${d.visitors} visiteurs`}
                    />
                  </div>
                  <div className="text-[10px] font-bold text-[#0F172A]">{d.views}</div>
                  <div className="text-[10px] text-[#475569] capitalize text-center leading-tight">{dayLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top pages */}
      <div>
        <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-widest mb-3">Pages les plus consultées (30 derniers jours)</h3>
        <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden" data-testid="analytics-top-pages">
          {stats.top_pages.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#475569]">Pas encore de données.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-[#475569]">
                  <th className="px-4 py-3 font-bold">#</th>
                  <th className="px-4 py-3 font-bold">Page</th>
                  <th className="px-4 py-3 font-bold text-right">Vues</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_pages.map((p, i) => (
                  <tr key={p.path} className="border-t border-[#E2E8F0] hover:bg-slate-50">
                    <td className="px-4 py-3 font-bold text-[#D97706]">{i + 1}</td>
                    <td className="px-4 py-3 font-mono text-[#0F172A]">{p.path === "/" ? "Accueil" : p.path}</td>
                    <td className="px-4 py-3 text-right font-bold">{p.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* All time + reset */}
      <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp className="text-[#1E3A8A]" />
          <div>
            <div className="text-xs text-[#475569]">Depuis le début (max 365 jours)</div>
            <div className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
              {stats.total.pageviews.toLocaleString("fr-FR")} vues · {stats.total.unique_visitors.toLocaleString("fr-FR")} visiteurs
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={reset} data-testid="analytics-reset" className="text-[#DC2626]">
          Tout réinitialiser
        </Button>
      </div>
    </div>
  );
}
